import { apiService } from "./apiService";

// Token management
const TOKEN_KEY = "accessToken";
const USER_KEY = "user";

// Set auth token for API requests
export const setAuthToken = (token: string | null) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

// Get stored user data
export const getCurrentUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

// Check if user is authenticated
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem(TOKEN_KEY);
};

// Clear auth data
export const clearAuth = () => {
  setAuthToken(null);
  localStorage.removeItem(USER_KEY);
};

export const authApi = {
  // User registration
  register: async (userData: any) => {
    try {
      interface ApiResponse<T = any> { success: boolean; message: string; data?: T }
      // Map frontend promoCode -> backend expects `promocode`
      const { promoCode, ...rest } = userData || {};
      const payload = {
        ...rest,
        ...(promoCode ? { promocode: promoCode } : {}),
      };
      // Note: backend wraps payload under { success, message, data }
      const response = await apiService.post<ApiResponse>("/auth/register", payload);
      console.log("Registration API response", response);
      return response; // do not store on signup; user is not logged in yet
    } catch (error) {
      throw error;
    }
  },

  // User login
  login: async (credentials: { email: string; password: string; isDigivision?: boolean }) => {
    try {
      console.log("Sending login request with credentials:", credentials);

      interface LoginResponse {
        user: any;
        tokens: {
          accessToken: string;
          refreshToken: string;
        };
      }
      interface ApiResponse<T = any> { success: boolean; message: string; data?: T }

      // API returns wrapper { success, message, data: { user, tokens } }
      const response = await apiService.post<ApiResponse<LoginResponse>>("/auth/login", credentials);
      const user = response?.data?.user;
      const tokens = response?.data?.tokens;
      console.log("Login API response:", response);

      if (!tokens?.accessToken || !user) {
        console.error("Invalid response structure:", response);
        throw new Error("Invalid response from server: Missing required fields");
      }

      setAuthToken(tokens.accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      document.cookie = `refreshToken=${tokens.refreshToken}; path=/; Secure; SameSite=Strict`;

      return response;
    } catch (error) {
      clearAuth();
      throw error;
    }
  },

  setAuthToken,

  // User logout
  logout: async () => {
    try {
      // Clear auth data from local storage
      clearAuth();

      // Clear refresh token cookie
      document.cookie =
        "refreshToken=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";

      return { success: true };
    } catch (error) {
      console.error("Error during logout:", error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser,

  // Check if user is authenticated
  isAuthenticated,

  // Request password reset
  forgotPassword: async (emailData: any) => {
    try {
      interface ApiResponse<T = any> { success: boolean; message: string; data?: T }
      const response = await apiService.post<ApiResponse>("/auth/request-password-reset", emailData);
      return response;
    } catch (error) {
      throw error;
    }
  },

  // Reset password
  resetPassword: async (resetData: any) => {
    try {
      return await apiService.post("/auth/reset-password", resetData);
    } catch (error) {
      throw error;
    }
  },

  // Change password (requires authentication)
  changePassword: async (passwordData: any) => {
    try {
      return await apiService.put("/user/change-password", passwordData);
    } catch (error) {
      throw error;
    }
  },

  // Google OAuth login
  loginWithGoogle: async () => {
    try {
      return await apiService.get("/auth/google");
    } catch (error) {
      console.error("Error with Google login:", error);
      throw error;
    }
  },

  // Google OAuth callback
  googleCallback: async () => {
    try {
      return await apiService.get("/auth/google/callback");
    } catch (error) {
      throw error;
    }
  },

  // Email verification
  verifyEmail: async (token: string) => {
    try {
      return await apiService.post("/auth/verify-email", { token });
    } catch (error) {
      throw error;
    }
  },

  // Resend verification email
  resendVerification: async (email: string) => {
    try {
      return await apiService.post("/auth/resend-verification", { email });
    } catch (error) {
      throw error;
    }
  },

  // Check email verification status
  checkVerificationStatus: async (email: string) => {
    try {
      return await apiService.get(`/auth/check-verification-status?email=${encodeURIComponent(email)}`);
    } catch (error) {
      throw error;
    }
  },
};
